import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { EmailTestModeBadge } from '../../components/revenue/EmailTestModeBadge';
import { useRevenueAccess } from '../../hooks/useRevenueAccess';
import { EmailTemplateEditor } from '../../components/revenue/EmailTemplateEditor';
import { sanitizeEmailHtml } from '../../utils/emailHtmlSanitize';

interface EmailTemplate {
  id: string;
  name: string;
  type: string;
  subject: string;
  body_html: string;
  body_text: string | null;
  created_at: string;
  updated_at: string;
}

const TYPE_LABELS: Record<string, string> = {
  initial: 'Initial Notification',
  reminder_1: 'Reminder 1',
  reminder_2: 'Reminder 2',
  reminder_3: 'Reminder 3',
  confirmation: 'Confirmation',
  invoice: 'Invoice (hotel send)',
  sales_manager_recap: 'Sales manager recap (zero realised)',
};

export default function TemplateManagement() {
  const { canMutateRevenue } = useRevenueAccess();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const fetchTemplates = async () => {
    try {
      const { data, error: err } = await supabase
        .from('revenue_collection_email_templates')
        .select('*')
        .order('type', { ascending: true });

      if (err) throw err;
      setTemplates(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleSave = async (t: Partial<EmailTemplate>) => {
    if (!canMutateRevenue) return;
    try {
      const payload = {
        name: t.name,
        type: t.type,
        subject: t.subject,
        body_html: t.body_html,
        body_text: t.body_text || null,
        updated_at: new Date().toISOString(),
      };

      if (editing) {
        const { error: err } = await supabase
          .from('revenue_collection_email_templates')
          .update(payload)
          .eq('id', editing.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase
          .from('revenue_collection_email_templates')
          .insert(payload);
        if (err) throw err;
      }
      setEditing(null);
      setIsCreating(false);
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleDelete = async (id: string) => {
    if (!canMutateRevenue) return;
    if (!confirm('Delete this template?')) return;
    try {
      const { error: err } = await supabase
        .from('revenue_collection_email_templates')
        .delete()
        .eq('id', id);
      if (err) throw err;
      setEditing(null);
      fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/revenue-collection" className="btn-icon-back" aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <h1 className="page-title">Email Templates</h1>
            <EmailTestModeBadge />
          </div>
          {canMutateRevenue ? (
            <button type="button" onClick={() => setIsCreating(true)} className="btn-primary h-10 py-2">
              <Plus className="w-4 h-4" aria-hidden /> New Template
            </button>
          ) : null}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-gray-500">Loading...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {templates.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{t.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{TYPE_LABELS[t.type] || t.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{t.subject}</td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => setEditing(t)} className="btn-xs-teal-outline">
                        {canMutateRevenue ? 'Edit' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/90 px-4 py-3 text-sm text-gray-600">
          <p className="font-medium text-gray-800">Placeholders</p>
          <ul className="mt-2 space-y-2">
            <li className="leading-relaxed">
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800 ring-1 ring-gray-200/80">
                {'{{hotel_name}}'}
              </code>{' '}
              — Hotel display name.
            </li>
            <li className="leading-relaxed">
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800 ring-1 ring-gray-200/80">
                {'{{campaign_period}}'}
              </code>{' '}
              — Campaign period label.
            </li>
            <li className="leading-relaxed">
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800 ring-1 ring-gray-200/80">
                {'{{link_url}}'}
              </code>{' '}
              — Public form or tracking link where the send applies one.
            </li>
            <li className="leading-relaxed">
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800 ring-1 ring-gray-200/80">
                {'{{groups_summary}}'}
              </code>{' '}
              — Groups and extra lines with realised amounts and commission (invoice emails).
            </li>
            <li className="leading-relaxed">
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800 ring-1 ring-gray-200/80">
                {'{{submission_summary}}'}
              </code>{' '}
              — Same submission data without commission (confirmation after hotelier validate).
            </li>
            <li className="leading-relaxed">
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800 ring-1 ring-gray-200/80">
                {'{{contact.invoice.first_name}}'}
              </code>
              {' and '}
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800 ring-1 ring-gray-200/80">
                {'{{contact.invoice.last_name}}'}
              </code>{' '}
              — {'Hotel\'s contacts with '}
              <code className="text-xs text-gray-800">contact_type = invoice</code>.
            </li>
          </ul>
        </div>
      </main>

      {(editing || (isCreating && canMutateRevenue)) && (
        <TemplateForm
          template={editing || undefined}
          readOnly={!canMutateRevenue}
          onSave={handleSave}
          onCancel={() => {
            setEditing(null);
            setIsCreating(false);
          }}
          onDelete={canMutateRevenue && editing ? () => handleDelete(editing.id) : undefined}
        />
      )}
    </div>
  );
}

function TemplateForm({
  template,
  onSave,
  onCancel,
  onDelete,
  readOnly = false,
}: {
  template?: EmailTemplate;
  onSave: (t: Partial<EmailTemplate>) => void;
  onCancel: () => void;
  onDelete?: () => void;
  readOnly?: boolean;
}) {
  const formKey = template?.id ?? 'new';
  const [name, setName] = useState(template?.name || '');
  const [type, setType] = useState(template?.type || 'initial');
  const [subject, setSubject] = useState(template?.subject || '');
  const [bodyHtml, setBodyHtml] = useState(template?.body_html || '');
  const [bodyText, setBodyText] = useState(template?.body_text || '');
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">
            {readOnly ? 'View Template' : template ? 'Edit Template' : 'New Template'}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                readOnly={readOnly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                disabled={!!template || readOnly}
              >
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                readOnly={readOnly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
              <p className="text-xs text-gray-500 mb-2">
                Rich text with safe HTML. Use <strong className="font-medium">HTML</strong> in the toolbar for raw markup.
              </p>
              <EmailTemplateEditor
                key={formKey}
                value={bodyHtml}
                onChange={setBodyHtml}
                showHtmlSource={showHtmlSource}
                onToggleSource={() => setShowHtmlSource((v) => !v)}
                readOnly={readOnly}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plain text fallback (optional)
              </label>
              <textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                placeholder="Leave empty to auto-generate from HTML when sending"
                readOnly={readOnly}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
            <div>
              {template && onDelete && !readOnly && (
                <button type="button" onClick={onDelete} className="btn-danger-soft">
                  <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
                  Delete template
                </button>
              )}
            </div>
            <div className="flex justify-end gap-2 ml-auto">
              <button type="button" onClick={onCancel} className="btn-teal-outline">
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() =>
                    onSave({
                      name,
                      type,
                      subject,
                      body_html: sanitizeEmailHtml(bodyHtml),
                      body_text: bodyText.trim() ? bodyText.trim() : null,
                    })
                  }
                  className="btn-primary"
                >
                  Save
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
